/**
 * Map an axios/network error to a user-facing Error with curated copy for the
 * common auth/permission/not-found statuses, falling back to the server's
 * `detail` or a caller-supplied message. Callers that surface the thrown
 * message directly to the user (e.g. a banner) should wrap their request in a
 * try/catch and rethrow via this helper rather than re-implementing the mapping.
 *
 * Kept free of `import.meta` (unlike lib/api) so it stays unit-testable in jest.
 */
export const toFriendlyApiError = (error: unknown, fallback: string): Error => {
  const err = error as {
    response?: { status?: number; data?: { detail?: string } };
  };
  if (err?.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 401) {
      return new Error("Authentication required. Please log in.");
    }
    if (status === 403) {
      return new Error(detail || "You don't have permission to view this data.");
    }
    if (status === 404) {
      return new Error(detail || "Not found.");
    }
    // Prefer the server's detail; otherwise the caller's friendly fallback
    // rather than axios's raw "Request failed with status code N".
    return new Error(detail || fallback);
  }
  return new Error(`${fallback} Please try again.`);
};
